(function () {
  'use strict';

  // The snippet and desktop lazy loader may request this same runtime. Reuse
  // the existing DOM owner instead of attaching another set of handlers.
  var existingController = window.TwistedSwagArtworkStudioAPI;
  if (existingController && existingController.controllerVersion === 'source-bound-v1' &&
      typeof existingController.initializeAllStudios === 'function') {
    existingController.initializeAllStudios();
    return;
  }

  var initializedStudios = new WeakSet();
  var vectorDispatchers = new WeakMap();
  var backgroundValidationRecords = new WeakMap();
  var backgroundRefusals = new Set();
  var backgroundRefusalSyncers = new WeakMap();
  function rememberBackgroundRefusal(hash) {
    backgroundRefusals.add(hash);
    document.querySelectorAll('[data-ts-background-source-sha256]').forEach(function (card) {
      var sync = backgroundRefusalSyncers.get(card);
      if (sync && card.getAttribute('data-ts-background-source-sha256') === hash) sync();
    });
  }
  var BACKGROUND_REFUSAL = 'This tool removes mostly solid or simple backgrounds. It can’t reliably separate subjects from detailed scenes such as gravel, grass or furniture. Your original image has not been changed. Upload artwork with a simpler background or continue with the Original.';
  function backgroundUnsupportedError() {
    var error = new Error(BACKGROUND_REFUSAL);
    error.errorClass = 'background_unsupported_input';
    return error;
  }
  async function pixelDigest(pixels) {
    var digest = await crypto.subtle.digest('SHA-256', pixels);
    return Array.from(new Uint8Array(digest), function (value) { return value.toString(16).padStart(2, '0'); }).join('');
  }

  var SUPPORTED_RASTER_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/bmp'
  ];

  var MAX_FILE_SIZE =
    10 * 1024 * 1024;

  function query(root, selector) {
    return root
      ? root.querySelector(selector)
      : null;
  }

  function queryAll(root, selector) {
    return root
      ? Array.prototype.slice.call(
          root.querySelectorAll(selector)
        )
      : [];
  }

  function setHidden(element, hidden) {
    if (!element) return;

    element.hidden =
      Boolean(hidden);
  }

  function setText(element, value) {
    if (!element) return;

    element.textContent =
      value;
  }

  function setInputValue(element, value) {
    if (!element) return;

    element.value =
      value === null ||
      value === undefined
        ? ''
        : String(value);
  }

  function clamp(value, minimum, maximum) {
    return Math.max(
      minimum,
      Math.min(
        maximum,
        value
      )
    );
  }

  function roundToTwo(value) {
    return Math.round(
      Number(value || 0) * 100
    ) / 100;
  }

  function formatFileSize(bytes) {
    var size =
      Number(bytes || 0);

    if (size < 1024) {
      return size + ' B';
    }

    if (
      size <
      1024 * 1024
    ) {
      return (
        size / 1024
      ).toFixed(1) + ' KB';
    }

    return (
      size /
      (
        1024 *
        1024
      )
    ).toFixed(1) + ' MB';
  }

  function getFileExtension(fileName) {
    var parts =
      String(
        fileName || ''
      ).split('.');

    return parts.length > 1
      ? parts.pop().toLowerCase()
      : '';
  }

  function isSvgFile(file) {
    if (!file) return false;

    return (
      file.type ===
        'image/svg+xml' ||
      getFileExtension(
        file.name
      ) === 'svg'
    );
  }

  function isSupportedRaster(file) {
    if (!file) return false;

    return (
      SUPPORTED_RASTER_TYPES.indexOf(
        file.type
      ) !== -1
    );
  }

  function createObjectUrl(fileOrBlob) {
    try {
      return URL.createObjectURL(
        fileOrBlob
      );
    } catch (error) {
      console.error(
        'Artwork preview URL error:',
        error
      );

      return '';
    }
  }

  function revokeObjectUrl(url) {
    if (!url) return;

    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        'Artwork preview cleanup error:',
        error
      );
    }
  }

  function hexToRgb(hex) {
    var clean =
      String(hex || '')
        .replace('#', '');

    if (clean.length === 3) {
      clean =
        clean
          .split('')
          .map(function (character) {
            return character +
              character;
          })
          .join('');
    }

    var parsed =
      parseInt(clean, 16);

    if (
      Number.isNaN(parsed)
    ) {
      return {
        red: 255,
        green: 255,
        blue: 255
      };
    }

    return {
      red:
        (
          parsed >> 16
        ) & 255,

      green:
        (
          parsed >> 8
        ) & 255,

      blue:
        parsed & 255
    };
  }

  function rgbToHex(
    red,
    green,
    blue
  ) {
    function component(value) {
      return clamp(
        Math.round(value),
        0,
        255
      )
        .toString(16)
        .padStart(2, '0');
    }

    return (
      '#' +
      component(red) +
      component(green) +
      component(blue)
    );
  }

  function colorDistance(
    red,
    green,
    blue,
    target
  ) {
    var redDifference =
      red - target.red;

    var greenDifference =
      green - target.green;

    var blueDifference =
      blue - target.blue;

    return Math.sqrt(
      redDifference *
        redDifference +
      greenDifference *
        greenDifference +
      blueDifference *
        blueDifference
    );
  }

  function buildEdgeConnectedBackgroundMask(
    imageData,
    width,
    height,
    options
  ) {
    options =
      options || {};

    var data =
      imageData &&
      imageData.data;

    if (
      !data ||
      !width ||
      !height ||
      data.length !==
        width *
        height *
        4
    ) {
      throw new Error(
        'The artwork pixels could not be inspected for background removal.'
      );
    }

    var pixelCount =
      width *
      height;

    var stripWidth =
      clamp(
        Math.round(
          Math.min(
            width,
            height
          ) *
            0.003
        ),
        2,
        4
      );

    var samples = [];

    var edgeTotals = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };

    function addSample(
      x,
      y,
      edge
    ) {
      var index =
        y *
          width +
        x;

      var offset =
        index *
        4;

      if (
        data[offset + 3] <
        16
      ) {
        return;
      }

      samples.push({
        index: index,
        edge: edge,
        red: data[offset],
        green: data[offset + 1],
        blue: data[offset + 2]
      });

      edgeTotals[edge] +=
        1;
    }

    for (
      var depth = 0;
      depth < stripWidth;
      depth += 1
    ) {
      for (
        var horizontal = 0;
        horizontal < width;
        horizontal += 1
      ) {
        addSample(
          horizontal,
          depth,
          'top'
        );

        addSample(
          horizontal,
          height -
            1 -
            depth,
          'bottom'
        );
      }

      for (
        var vertical = stripWidth;
        vertical <
          height -
            stripWidth;
        vertical += 1
      ) {
        addSample(
          depth,
          vertical,
          'left'
        );

        addSample(
          width -
            1 -
            depth,
          vertical,
          'right'
        );
      }
    }

    if (
      samples.length <
      16
    ) {
      throw backgroundUnsupportedError();
    }

    var buckets =
      Object.create(
        null
      );

    samples.forEach(
      function (sample) {
        var key =
          Math.floor(
            sample.red /
              32
          ) +
          ':' +
          Math.floor(
            sample.green /
              32
          ) +
          ':' +
          Math.floor(
            sample.blue /
              32
          );

        var bucket =
          buckets[key];

        if (!bucket) {
          bucket = {
            count: 0,
            red: 0,
            green: 0,
            blue: 0
          };

          buckets[key] =
            bucket;
        }

        bucket.count +=
          1;

        bucket.red +=
          sample.red;

        bucket.green +=
          sample.green;

        bucket.blue +=
          sample.blue;
      }
    );

    var bucketList =
      Object.keys(
        buckets
      ).map(
        function (key) {
          var bucket =
            buckets[key];

          return {
            count:
              bucket.count,
            red:
              bucket.red /
              bucket.count,
            green:
              bucket.green /
              bucket.count,
            blue:
              bucket.blue /
              bucket.count
          };
        }
      );

    bucketList.sort(
      function (
        first,
        second
      ) {
        return second.count -
          first.count;
      }
    );

    var selectedBucket =
      bucketList[0];

    var preferredTarget =
      options.preferredTarget;

    if (
      options.preferTarget &&
      preferredTarget
    ) {
      var maximumPreferredDistance =
        Math.max(
          48,
          Number(
            options.tolerance ||
              32
          ) *
            1.5
        );

      var preferredBuckets =
        bucketList.filter(
          function (bucket) {
            return (
              bucket.count /
                samples.length >=
                0.03 &&
              colorDistance(
                bucket.red,
                bucket.green,
                bucket.blue,
                preferredTarget
              ) <=
                maximumPreferredDistance
            );
          }
        );

      if (
        preferredBuckets.length
      ) {
        selectedBucket =
          preferredBuckets[0];
      } else {
        throw new Error(
          'A confident exterior background matching the selected color could not be detected.'
        );
      }
    }

    var model = {
      red:
        selectedBucket.red,
      green:
        selectedBucket.green,
      blue:
        selectedBucket.blue
    };

    var cluster = [];

    for (
      var refinement = 0;
      refinement < 2;
      refinement += 1
    ) {
      cluster =
        samples.filter(
          function (sample) {
            return colorDistance(
              sample.red,
              sample.green,
              sample.blue,
              model
            ) <=
              30;
          }
        );

      if (!cluster.length) {
        break;
      }

      model = {
        red:
          cluster.reduce(
            function (
              total,
              sample
            ) {
              return total +
                sample.red;
            },
            0
          ) /
          cluster.length,

        green:
          cluster.reduce(
            function (
              total,
              sample
            ) {
              return total +
                sample.green;
            },
            0
          ) /
          cluster.length,

        blue:
          cluster.reduce(
            function (
              total,
              sample
            ) {
              return total +
                sample.blue;
            },
            0
          ) /
          cluster.length
      };
    }

    if (!cluster.length) {
      throw backgroundUnsupportedError();
    }

    var variance =
      cluster.reduce(
        function (
          total,
          sample
        ) {
          var distance =
            colorDistance(
              sample.red,
              sample.green,
              sample.blue,
              model
            );

          return total +
            distance *
            distance;
        },
        0
      ) /
      cluster.length;

    var standardDeviation =
      Math.sqrt(
        variance
      );

    // This is color-based exterior removal, not semantic subject segmentation.
    // The old confidence test measured only a selected color cluster, allowing
    // textured photographs to pass and lose scattered patches of background.
    // A conservative near-uniform color boundary must run before any alpha
    // changes, preview replacement or stored-file upload. Increasing the user's
    // tolerance must not turn a complex scene into a claimed safe cutout.
    if (standardDeviation > 12) {
      throw backgroundUnsupportedError();
    }

    var edgeMatches = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };

    cluster.forEach(
      function (sample) {
        edgeMatches[sample.edge] +=
          1;
      }
    );

    var edgeCoverage = {};

    Object.keys(
      edgeTotals
    ).forEach(
      function (edge) {
        edgeCoverage[edge] =
          edgeTotals[edge]
            ? edgeMatches[edge] /
              edgeTotals[edge]
            : 0;
      }
    );

    var coverage =
      cluster.length /
      samples.length;

    var substantialEdgeCount =
      Object.keys(
        edgeCoverage
      ).filter(
        function (edge) {
          return edgeCoverage[edge] >=
            0.2;
        }
      ).length;

    var minimumCoverage =
      options.preferTarget
        ? 0.12
        : 0.25;

    if (
      coverage <
        minimumCoverage ||
      standardDeviation >
        24 ||
      substantialEdgeCount <
        2
    ) {
      throw backgroundUnsupportedError();
    }

    function distanceAt(
      pixelIndex
    ) {
      var offset =
        pixelIndex *
        4;

      return colorDistance(
        data[offset],
        data[offset + 1],
        data[offset + 2],
        model
      );
    }

    if (options.assessmentOnly) return { supported: true, standardDeviation: standardDeviation, borderCoverage: coverage };

    var requestedTolerance =
      clamp(
        Number(
          options.tolerance ||
            32
        ),
        8,
        64
      );

    var seedTolerance =
      clamp(
        standardDeviation *
          2.5 +
          10,
        18,
        Math.max(
          22,
          requestedTolerance
        )
      );

    var growthTolerance =
      clamp(
        Math.max(
          requestedTolerance,
          seedTolerance +
            8,
          standardDeviation *
            4 +
            16
        ),
        seedTolerance +
          8,
        56
      );

    var maximumSeedCount =
      width *
        2 +
      Math.max(
        0,
        height -
          2
      ) *
        2;

    var seeds =
      new Int32Array(
        maximumSeedCount
      );

    var seedCount =
      0;

    function addSeed(
      pixelIndex
    ) {
      if (
        distanceAt(
          pixelIndex
        ) >
        seedTolerance
      ) {
        return;
      }

      seeds[seedCount] =
        pixelIndex;

      seedCount +=
        1;
    }

    for (
      var seedX = 0;
      seedX < width;
      seedX += 1
    ) {
      addSeed(
        seedX
      );

      addSeed(
        (
          height -
            1
        ) *
          width +
          seedX
      );
    }

    for (
      var seedY = 1;
      seedY <
        height -
          1;
      seedY += 1
    ) {
      addSeed(
        seedY *
          width
      );

      addSeed(
        seedY *
          width +
          width -
          1
      );
    }

    if (
      seedCount <
      Math.max(
        8,
        Math.floor(
          maximumSeedCount *
            0.05
        )
      )
    ) {
      throw backgroundUnsupportedError();
    }

    var visited =
      new Uint8Array(
        pixelCount
      );

    var queue =
      new Int32Array(
        pixelCount
      );

    var mask =
      new Uint8Array(
        pixelCount
      );

    var largestComponentSize =
      0;

    for (
      var seedIndex = 0;
      seedIndex < seedCount;
      seedIndex += 1
    ) {
      var componentSeed =
        seeds[seedIndex];

      if (
        visited[componentSeed]
      ) {
        continue;
      }

      var queueHead =
        0;

      var queueTail =
        0;

      visited[componentSeed] =
        1;

      queue[queueTail] =
        componentSeed;

      queueTail +=
        1;

      while (
        queueHead <
        queueTail
      ) {
        var current =
          queue[queueHead];

        queueHead +=
          1;

        var currentX =
          current %
          width;

        var currentY =
          Math.floor(
            current /
              width
          );

        var neighborCount =
          0;

        var neighbors = [
          0,
          0,
          0,
          0
        ];

        if (
          currentX > 0
        ) {
          neighbors[neighborCount] =
            current -
            1;

          neighborCount +=
            1;
        }

        if (
          currentX +
            1 <
          width
        ) {
          neighbors[neighborCount] =
            current +
            1;

          neighborCount +=
            1;
        }

        if (
          currentY > 0
        ) {
          neighbors[neighborCount] =
            current -
            width;

          neighborCount +=
            1;
        }

        if (
          currentY +
            1 <
          height
        ) {
          neighbors[neighborCount] =
            current +
            width;

          neighborCount +=
            1;
        }

        for (
          var neighborIndex = 0;
          neighborIndex <
            neighborCount;
          neighborIndex += 1
        ) {
          var neighbor =
            neighbors[neighborIndex];

          if (
            visited[neighbor] ||
            distanceAt(
              neighbor
            ) >
              growthTolerance
          ) {
            continue;
          }

          visited[neighbor] =
            1;

          queue[queueTail] =
            neighbor;

          queueTail +=
            1;
        }
      }

      if (
        queueTail >
        largestComponentSize
      ) {
        mask.fill(
          0
        );

        for (
          var componentIndex = 0;
          componentIndex <
            queueTail;
          componentIndex += 1
        ) {
          mask[
            queue[componentIndex]
          ] =
            1;
        }

        largestComponentSize =
          queueTail;
      }
    }

    var minimumMaskSize =
      Math.max(
        16,
        Math.floor(
          pixelCount *
            0.01
        )
      );

    if (
      largestComponentSize <
      minimumMaskSize
    ) {
      throw backgroundUnsupportedError();
    }

    var connectedEdgeCounts = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };

    for (
      var connectedX = 0;
      connectedX < width;
      connectedX += 1
    ) {
      if (
        mask[connectedX]
      ) {
        connectedEdgeCounts.top +=
          1;
      }

      if (
        mask[
          (
            height -
              1
          ) *
            width +
            connectedX
        ]
      ) {
        connectedEdgeCounts.bottom +=
          1;
      }
    }

    for (
      var connectedY = 1;
      connectedY <
        height -
          1;
      connectedY += 1
    ) {
      if (
        mask[
          connectedY *
            width
        ]
      ) {
        connectedEdgeCounts.left +=
          1;
      }

      if (
        mask[
          connectedY *
            width +
            width -
            1
        ]
      ) {
        connectedEdgeCounts.right +=
          1;
      }
    }

    var connectedEdgeCoverage = {
      top:
        connectedEdgeCounts.top /
        width,
      right:
        connectedEdgeCounts.right /
        Math.max(
          1,
          height -
            2
        ),
      bottom:
        connectedEdgeCounts.bottom /
        width,
      left:
        connectedEdgeCounts.left /
        Math.max(
          1,
          height -
            2
        )
    };

    var connectedEdgeCount =
      Object.keys(
        connectedEdgeCoverage
      ).filter(
        function (edge) {
          return connectedEdgeCoverage[edge] >=
            0.1;
        }
      ).length;

    if (
      connectedEdgeCount <
      2
    ) {
      throw backgroundUnsupportedError();
    }

    var holeFillCount =
      0;

    for (
      var holePass = 0;
      holePass < 2;
      holePass += 1
    ) {
      var additions = [];

      for (
        var holeY = 1;
        holeY <
          height -
            1;
        holeY += 1
      ) {
        for (
          var holeX = 1;
          holeX <
            width -
              1;
          holeX += 1
        ) {
          var holeIndex =
            holeY *
              width +
            holeX;

          if (
            mask[holeIndex]
          ) {
            continue;
          }

          var matchingNeighbors =
            mask[
              holeIndex -
                1
            ] +
            mask[
              holeIndex +
                1
            ] +
            mask[
              holeIndex -
                width
            ] +
            mask[
              holeIndex +
                width
            ];

          if (
            matchingNeighbors >=
              3 &&
            distanceAt(
              holeIndex
            ) <=
              growthTolerance +
                12
          ) {
            additions.push(
              holeIndex
            );
          }
        }
      }

      additions.forEach(
        function (pixelIndex) {
          mask[pixelIndex] =
            1;
        }
      );

      holeFillCount +=
        additions.length;
    }

    var removedPixelCount =
      0;

    var proposedRemovalCount = 0;
    for (var proposedIndex = 0; proposedIndex < pixelCount; proposedIndex += 1) {
      if (mask[proposedIndex]) proposedRemovalCount += 1;
    }
    if (pixelCount - proposedRemovalCount < Math.max(4, Math.floor(pixelCount * 0.01))) {
      var missingForeground = new Error('A distinct foreground could not be preserved. Your original artwork has not been changed.');
      missingForeground.errorClass = 'result_mismatch';
      missingForeground.novaArtworkValidationRule = 'background_foreground';
      throw missingForeground;
    }

    for (
      var maskedIndex = 0;
      maskedIndex < pixelCount;
      maskedIndex += 1
    ) {
      if (
        !mask[maskedIndex]
      ) {
        continue;
      }

      data[
        maskedIndex *
          4 +
          3
      ] =
        0;

      removedPixelCount +=
        1;
    }

    var featherRadius =
      clamp(
        Math.round(
          Number(
            options.cleanup ||
              0
          )
        ),
        0,
        2
      );

    var featheredPixelCount =
      0;

    var featherFrontier =
      mask.slice();

    for (
      var featherPass = 0;
      featherPass <
        featherRadius;
      featherPass += 1
    ) {
      var nextFrontier =
        new Uint8Array(
          pixelCount
        );

      for (
        var featherIndex = 0;
        featherIndex < pixelCount;
        featherIndex += 1
      ) {
        if (
          mask[featherIndex]
        ) {
          continue;
        }

        var featherX =
          featherIndex %
          width;

        var featherY =
          Math.floor(
            featherIndex /
              width
          );

        var touchesFrontier =
          (
            featherX > 0 &&
            featherFrontier[
              featherIndex -
                1
            ]
          ) ||
          (
            featherX +
              1 <
              width &&
            featherFrontier[
              featherIndex +
                1
            ]
          ) ||
          (
            featherY > 0 &&
            featherFrontier[
              featherIndex -
                width
            ]
          ) ||
          (
            featherY +
              1 <
              height &&
            featherFrontier[
              featherIndex +
                width
            ]
          );

        if (!touchesFrontier) {
          continue;
        }

        var featherDistance =
          distanceAt(
            featherIndex
          );

        var featherLimit =
          growthTolerance +
          14 +
          featherPass *
            6;

        if (
          featherDistance >
          featherLimit
        ) {
          continue;
        }

        var featherAlpha =
          clamp(
            Math.round(
              64 +
              (
                featherDistance -
                  growthTolerance
              ) /
                Math.max(
                  1,
                  featherLimit -
                    growthTolerance
                ) *
                191
            ),
            64,
            255
          );

        var alphaOffset =
          featherIndex *
            4 +
          3;

        if (
          featherAlpha <
          data[alphaOffset]
        ) {
          data[alphaOffset] =
            featherAlpha;

          featheredPixelCount +=
            1;

          nextFrontier[featherIndex] =
            1;
        }
      }

      featherFrontier =
        nextFrontier;
    }

    return {
      backgroundColor:
        rgbToHex(
          model.red,
          model.green,
          model.blue
        ),
      backgroundRgb: {
        red:
          roundToTwo(
            model.red
          ),
        green:
          roundToTwo(
            model.green
          ),
        blue:
          roundToTwo(
            model.blue
          )
      },
      stripWidth:
        stripWidth,
      borderSampleCount:
        samples.length,
      clusterSampleCount:
        cluster.length,
      borderCoverage:
        roundToTwo(
          coverage *
            100
        ),
      variance:
        roundToTwo(
          variance
        ),
      standardDeviation:
        roundToTwo(
          standardDeviation
        ),
      edgeCoverage: {
        top:
          roundToTwo(
            edgeCoverage.top *
              100
          ),
        right:
          roundToTwo(
            edgeCoverage.right *
              100
          ),
        bottom:
          roundToTwo(
            edgeCoverage.bottom *
              100
          ),
        left:
          roundToTwo(
            edgeCoverage.left *
              100
          )
      },
      connectedEdgeCoverage: {
        top:
          roundToTwo(
            connectedEdgeCoverage.top *
              100
          ),
        right:
          roundToTwo(
            connectedEdgeCoverage.right *
              100
          ),
        bottom:
          roundToTwo(
            connectedEdgeCoverage.bottom *
              100
          ),
        left:
          roundToTwo(
            connectedEdgeCoverage.left *
              100
          )
      },
      substantialEdgeCount:
        substantialEdgeCount,
      connectedEdgeCount:
        connectedEdgeCount,
      seedTolerance:
        roundToTwo(
          seedTolerance
        ),
      growthTolerance:
        roundToTwo(
          growthTolerance
        ),
      seedCount:
        seedCount,
      connectedRemovedPixelCount:
        largestComponentSize,
      removedPixelCount:
        removedPixelCount,
      holeFillCount:
        holeFillCount,
      featheredPixelCount:
        featheredPixelCount,
      confidence:
        true
    };
  }

  function loadImageFromUrl(url) {
    return new Promise(
      function (resolve, reject) {
        var image =
          new Image();

        image.onload =
          function () {
            resolve(image);
          };

        image.onerror =
          function () {
            reject(
              new Error(
                'The artwork preview could not be opened.'
              )
            );
          };

        image.src =
          url;
      }
    );
  }

  function canvasToBlob(
    canvas,
    type,
    quality
  ) {
    return new Promise(
      function (resolve, reject) {
        canvas.toBlob(
          function (blob) {
            if (!blob) {
              reject(
                new Error(
                  'The prepared artwork file could not be created.'
                )
              );

              return;
            }

            resolve(blob);
          },
          type || 'image/png',
          quality || 0.95
        );
      }
    );
  }

  function getProductForm(studio) {
    var section =
      studio.closest(
        '[id^="ProductSection-"]'
      );

    return section
      ? section.querySelector(
          'form[action*="/cart/add"]'
        )
      : null;
  }

  function movePropertiesIntoProductForm(
    studio
  ) {
    var form =
      getProductForm(studio);

    if (!form) return;

    if (!form.id) {
      form.id =
        'TwistedSwagProductForm-' +
        Math.random()
          .toString(36)
          .slice(2);
    }

    queryAll(
      studio,
      '.ts-artwork-card__order-properties input'
    ).forEach(
      function (input) {
        /*
          Keep each artwork property input inside its original card
          so the uploader, analyzer, background remover, vectorizer,
          and version selector can continue updating the correct field.

          The HTML form attribute connects the input to Shopify's
          product form without physically moving it out of the card.
        */
        input.setAttribute(
          'form',
          form.id
        );
      }
    );
  }

  function initializeStudio(studio) {
    if (
      !studio ||
      initializedStudios.has(
        studio
      )
    ) {
      return;
    }

    initializedStudios.add(
      studio
    );

    var workerUrl =
      String(
        studio.getAttribute(
          'data-worker-url'
        ) || ''
      ).replace(
        /\/vectorize\/?$/,
        ''
      );

    var vectorEndpoint =
      workerUrl +
      '/vectorize';

    var storageEndpoint =
      workerUrl +
      '/store-file';

    var cards =
      queryAll(
        studio,
        '[data-ts-artwork-card]'
      );

    var studioStatus =
      query(
        studio,
        '[data-ts-studio-status]'
      );

    var fileCount =
      query(
        studio,
        '[data-ts-file-count]'
      );

    var choiceCount =
      query(
        studio,
        '[data-ts-choice-count]'
      );

    var modal =
      document.querySelector(
        '[data-ts-studio-modal]'
      );

    var modalImage =
      query(
        modal,
        '[data-ts-modal-image]'
      );

    var modalTitle =
      query(
        modal,
        '[data-ts-modal-title]'
      );

    var modalClose =
      query(
        modal,
        '[data-ts-modal-close]'
      );

    var previousFocusedElement =
      null;

    var states = cards.map(
      function (card) {
        var backgroundActions = query(card, '.ts-background-actions');
        if (backgroundActions && !query(card, '[data-ts-background-guidance]')) {
          var backgroundGuidance = document.createElement('p');
          backgroundGuidance.setAttribute('data-ts-background-guidance', '');
          backgroundGuidance.textContent = 'Works best with solid or simple backgrounds. Not suitable for extracting people, pets or objects from complex photographic scenes.';
          backgroundActions.parentNode.insertBefore(backgroundGuidance, backgroundActions);
        }
        return {
          card:
            card,

          number:
            Number(
              card.getAttribute(
                'data-artwork-number'
              ) || 1
            ),

          file:
            null,

          originalUrl:
            '',

          currentPreviewUrl:
            '',

          backgroundUrl:
            '',

          vectorUrl:
            '',

          originalStoredUrl:
            '',

          backgroundStoredUrl:
            '',

          naturalWidth:
            0,

          naturalHeight:
            0,

          aspectRatio:
            1,

          isVector:
            false,

          isLocked:
            true,

          selectedVersion:
            'original',

          backgroundBlob:
            null,

          analysis:
            null,

          progressTimer:
            null
        };
      }
    );

    function getStateFromElement(element) {
      var card =
        element.closest(
          '[data-ts-artwork-card]'
        );

      if (!card) return null;

      return states.find(
        function (state) {
          return (
            state.card ===
            card
          );
        }
      ) || null;
    }

    function closeAllTooltips(
      exceptButton
    ) {
      queryAll(
        studio,
        '[data-ts-tooltip-button]'
      ).forEach(
        function (button) {
          if (
            exceptButton &&
            button ===
              exceptButton
          ) {
            return;
          }

          button.setAttribute(
            'aria-expanded',
            'false'
          );

          var tooltip =
            button.parentElement
              ? query(
                  button.parentElement,
                  '[data-ts-tooltip]'
                )
              : null;

          setHidden(
            tooltip,
            true
          );
        }
      );
    }

    function toggleTooltip(button) {
      var tooltip =
        button.parentElement
          ? query(
              button.parentElement,
              '[data-ts-tooltip]'
            )
          : null;

      if (!tooltip) return;

      var isOpening =
        button.getAttribute(
          'aria-expanded'
        ) !== 'true';

      closeAllTooltips(
        isOpening
          ? button
          : null
      );

      button.setAttribute(
        'aria-expanded',
        isOpening
          ? 'true'
          : 'false'
      );

      setHidden(
        tooltip,
        !isOpening
      );
    }

    function openModal(
      imageUrl,
      title
    ) {
      if (
        !modal ||
        !modalImage ||
        !imageUrl
      ) {
        return;
      }

      previousFocusedElement =
        document.activeElement;

      modalImage.src =
        imageUrl;

      modalImage.alt =
        title ||
        'Artwork preview';

      setText(
        modalTitle,
        title ||
        'Artwork Preview'
      );

      modal.classList.add(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'ts-studio-modal-open'
      );

      if (modalClose) {
        modalClose.focus();
      }
    }

    function closeModal() {
      if (!modal) return;

      modal.classList.remove(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.classList.remove(
        'ts-studio-modal-open'
      );

      if (modalImage) {
        modalImage.removeAttribute(
          'src'
        );
      }

      if (
        previousFocusedElement &&
        typeof previousFocusedElement.focus ===
          'function'
      ) {
        previousFocusedElement.focus();
      }
    }

    function activateNextCard(state) {
      var nextState =
        states[state.number];

      if (
        nextState &&
        !nextState.card.classList.contains(
          'is-active'
        )
      ) {
        nextState.card.classList.add(
          'is-active'
        );
      }
    }

    function updateSummary() {
      var populated =
        states.filter(
          function (state) {
            return Boolean(
              state.file
            );
          }
        );

      var completedChoices =
        populated.filter(
          function (state) {
            return Boolean(
              state.selectedVersion
            );
          }
        );

      setText(
        fileCount,
        populated.length +
          ' of 8'
      );

      setText(
        choiceCount,
        String(
          completedChoices.length
        )
      );

      if (
        populated.length === 0
      ) {
        setText(
          studioStatus,
          'Waiting for artwork'
        );
      } else if (
        populated.length === 1
      ) {
        setText(
          studioStatus,
          '1 artwork file ready'
        );
      } else {
        setText(
          studioStatus,
          populated.length +
            ' artwork files ready'
        );
      }
    }

    function clearProgressTimer(state) {
      var progress = state.card && query(state.card, '[data-ts-vector-progress]');
      if (progress) progress.setAttribute('aria-busy', 'false');
      if (state.vectorProgressAnimation) {
        state.vectorProgressAnimation.cancel();
        state.vectorProgressAnimation = null;
      }
      if (
        state.progressTimer
      ) {
        window.clearInterval(
          state.progressTimer
        );

        state.progressTimer =
          null;
      }
    }

    function resetStateUrls(state) {
      revokeObjectUrl(
        state.originalUrl
      );

      if (
        state.currentPreviewUrl &&
        state.currentPreviewUrl !==
          state.originalUrl
      ) {
        revokeObjectUrl(
          state.currentPreviewUrl
        );
      }

      if (
        state.backgroundUrl &&
        state.backgroundUrl !==
          state.originalUrl &&
        state.backgroundUrl !==
          state.currentPreviewUrl
      ) {
        revokeObjectUrl(
          state.backgroundUrl
        );
      }

      state.originalUrl =
        '';

      state.currentPreviewUrl =
        '';

      state.backgroundUrl =
        '';
    }

    function clearCardUi(state) {
      var card =
        state.card;

      clearProgressTimer(
        state
      );

      resetStateUrls(
        state
      );

      state.file =
        null;

      state.backgroundBlob =
        null;

      state.vectorUrl =
        '';

      state.originalStoredUrl =
        '';

      state.backgroundStoredUrl =
        '';

      state.naturalWidth =
        0;

      state.naturalHeight =
        0;

      state.aspectRatio =
        1;

      state.isVector =
        false;

      state.isLocked =
        true;

      state.selectedVersion =
        'original';

      state.analysis =
        null;

      card.classList.remove(
        'has-file'
      );

      var input =
        query(
          card,
          '[data-ts-artwork-input]'
        );

      if (input) {
        input.value =
          '';
      }

      setHidden(
        query(
          card,
          '[data-ts-artwork-workspace]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-upload-area]'
        ),
        false
      );

      setText(
        query(
          card,
          '[data-ts-file-status]'
        ),
        'No file selected'
      );

      setText(
        query(
          card,
          '[data-ts-artwork-title]'
        ),
        state.number === 1
          ? 'Choose your first artwork file'
          : 'Additional artwork'
      );

      setText(
        query(
          card,
          '[data-ts-upload-button-text]'
        ),
        'Choose Artwork ' +
          state.number
      );

      [
        '[data-ts-artwork-preview-image]',
        '[data-ts-version-original-image]',
        '[data-ts-version-background-image]',
        '[data-ts-version-vector-image]'
      ].forEach(
        function (selector) {
          var image =
            query(
              card,
              selector
            );

          if (image) {
            image.removeAttribute(
              'src'
            );
          }
        }
      );

      setText(
        query(
          card,
          '[data-ts-file-type]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-file-size]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-pixel-dimensions]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-transparency-status]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-dominant-colors]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-printable-colors]'
        ),
        '—'
      );

      setInputValue(
        query(
          card,
          '[data-ts-print-width]'
        ),
        ''
      );

      setInputValue(
        query(
          card,
          '[data-ts-print-height]'
        ),
        ''
      );

      var locationSelect =
        query(
          card,
          '[data-ts-location-select]'
        );

      if (locationSelect) {
        locationSelect.value =
          '';
      }

      var lockButton =
        query(
          card,
          '[data-ts-aspect-lock]'
        );

      if (lockButton) {
        lockButton.classList.add(
          'is-locked'
        );

        lockButton.setAttribute(
          'aria-pressed',
          'true'
        );
      }

      setText(
        query(
          card,
          '[data-ts-aspect-lock-text]'
        ),
        'Locked'
      );

      setText(
        query(
          card,
          '[data-ts-effective-dpi]'
        ),
        '—'
      );

      setText(
        query(
          card,
          '[data-ts-maximum-size]'
        ),
        '—'
      );

      updateQuality(
        state,
        'waiting',
        'Waiting',
        '',
        ''
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="background_removed"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="vector"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-vector-progress]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-vector-error]'
        ),
        true
      );

      var restoreButton =
        query(
          card,
          '[data-ts-restore-original]'
        );

      if (restoreButton) {
        restoreButton.disabled =
          true;
      }

      selectVersion(
        state,
        'original'
      );

      clearProperties(
        state
      );

      updateSummary();
    }

    function clearProperties(state) {
      var card =
        state.card;

      [
        '[data-ts-property-original-file]',
        '[data-ts-property-production-file]',
        '[data-ts-property-location]',
        '[data-ts-property-width]',
        '[data-ts-property-height]',
        '[data-ts-property-dpi]',
        '[data-ts-property-quality]',
        '[data-ts-property-vector-url]'
      ].forEach(
        function (selector) {
          setInputValue(
            query(
              card,
              selector
            ),
            ''
          );
        }
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-version]'
        ),
        'Original Artwork'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-background-status]'
        ),
        'No'
      );
    }

    function serviceBoundary(state, tool, stage, errorClass, status, exception, rule) {
      var card = state.card;
      card.setAttribute('data-ts-service-tool', tool);
      card.setAttribute('data-ts-service-stage', stage);
      card.setAttribute('data-ts-service-state', errorClass ? 'error' : stage === 'service-result' ? 'ready' : 'processing');
      card.setAttribute('data-ts-service-error', errorClass || '');
      card.setAttribute('data-ts-service-status', Number.isInteger(status) ? String(status) : '');
      card.setAttribute('data-ts-service-validation-rule', rule === 'background_foreground' ? rule : '');
      var safeException = ['Error','TypeError','ReferenceError','RangeError','SecurityError','EncodingError','NotSupportedError','NetworkError','AbortError','TimeoutError'].indexOf(exception) >= 0 ? exception : '';
      card.setAttribute('data-ts-service-exception', safeException);
      var diagnostic = window.NovaUpscaleSafeDiagnostics;
      if (diagnostic && diagnostic.enabled) diagnostic.capture(stage, {tool:tool,event:errorClass ? 'failed' : stage === 'service-result' ? 'ready' : 'running',errorClass:errorClass,status:status,exception:safeException});
    }

    async function uploadStoredFile(
      state,
      blobOrFile,
      versionType,
      fileName
    ) {
      var api = window.NovaAiGenerationAPI;
      if (!api || typeof api.projectArtworkService !== 'function') throw new Error('Reopen this project in the updated Design Studio to store artwork securely.');
      var result = await api.projectArtworkService(blobOrFile, false);
      state.card.setAttribute('data-ts-private-artifact', JSON.stringify(result.privateArtifact));
      return result.fileUrl;
    }

    async function storeOriginalFile(state) {
      if (
        !state.file ||
        state.originalStoredUrl
      ) {
        return;
      }

      try {
        state.originalStoredUrl =
          await uploadStoredFile(
            state,
            state.file,
            'original',
            state.file.name
          );

        setInputValue(
          query(
            state.card,
            '[data-ts-property-original-file]'
          ),
          state.originalStoredUrl
        );

        if (
          state.selectedVersion ===
          'original'
        ) {
          setInputValue(
            query(
              state.card,
              '[data-ts-property-production-file]'
            ),
            state.originalStoredUrl
          );
        }
      } catch (error) {
        console.error(
          'Original artwork storage error:',
          error
        );
      }
    }

    function selectVersion(
      state,
      version
    ) {
      var card =
        state.card;

      state.selectedVersion =
        version;

      queryAll(
        card,
        '[data-ts-version-option]'
      ).forEach(
        function (option) {
          var optionVersion =
            option.getAttribute(
              'data-ts-version-option'
            );

          option.classList.toggle(
            'is-selected',
            optionVersion ===
              version
          );

          var input =
            query(
              option,
              '[data-ts-version-choice]'
            );

          if (input) {
            input.checked =
              optionVersion ===
              version;
          }
        }
      );

      var versionLabel =
        'Original Artwork';

      var productionUrl =
        state.originalStoredUrl ||
        '';

      if (
        version ===
        'background_removed'
      ) {
        versionLabel =
          'Background Removed';

        productionUrl =
          state.backgroundStoredUrl ||
          '';
      }

      if (
        version ===
        'vector'
      ) {
        versionLabel =
          'Enhanced Vector';

        productionUrl =
          state.vectorUrl ||
          '';
      }

      setText(
        query(
          card,
          '[data-ts-selected-version]'
        ),
        versionLabel
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-version]'
        ),
        versionLabel
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-production-file]'
        ),
        productionUrl
      );

      updateSummary();
    }

    function updateQuality(
      state,
      level,
      label,
      title,
      description
    ) {
      var card =
        state.card;

      var pill =
        query(
          card,
          '[data-ts-quality-pill]'
        );

      var message =
        query(
          card,
          '[data-ts-quality-message]'
        );

      if (pill) {
        pill.classList.remove(
          'is-success',
          'is-info',
          'is-warning',
          'is-danger'
        );

        if (
          level ===
          'success'
        ) {
          pill.classList.add(
            'is-success'
          );
        } else if (
          level ===
          'info'
        ) {
          pill.classList.add(
            'is-info'
          );
        } else if (
          level ===
          'warning'
        ) {
          pill.classList.add(
            'is-warning'
          );
        } else if (
          level ===
          'danger'
        ) {
          pill.classList.add(
            'is-danger'
          );
        }

        pill.textContent =
          label;
      }

      if (message) {
        message.classList.remove(
          'is-success',
          'is-info'
        );

        if (
          level ===
          'success'
        ) {
          message.classList.add(
            'is-success'
          );
        }

        if (
          level ===
          'info'
        ) {
          message.classList.add(
            'is-info'
          );
        }

        setHidden(
          message,
          !title
        );
      }

      setText(
        query(
          card,
          '[data-ts-quality-title]'
        ),
        title
      );

      setText(
        query(
          card,
          '[data-ts-quality-description]'
        ),
        description
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-quality]'
        ),
        label
      );
    }

    function updateSizeAndQuality(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var width =
        parseFloat(
          widthInput
            ? widthInput.value
            : ''
        );

      var height =
        parseFloat(
          heightInput
            ? heightInput.value
            : ''
        );

      if (
        !width ||
        !height ||
        width <= 0 ||
        height <= 0
      ) {
        setText(
          query(
            card,
            '[data-ts-effective-dpi]'
          ),
          '—'
        );

        updateQuality(
          state,
          'waiting',
          'Waiting',
          '',
          ''
        );

        return;
      }

      var dpi;

      if (
        state.isVector
      ) {
        dpi =
          Infinity;
      } else {
        dpi =
          Math.min(
            state.naturalWidth /
              width,
            state.naturalHeight /
              height
          );
      }

      setText(
        query(
          card,
          '[data-ts-effective-dpi]'
        ),
        state.isVector
          ? 'Scalable'
          : Math.round(dpi) +
            ' DPI'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-width]'
        ),
        roundToTwo(width) +
          ' in'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-height]'
        ),
        roundToTwo(height) +
          ' in'
      );

      setInputValue(
        query(
          card,
          '[data-ts-property-dpi]'
        ),
        state.isVector
          ? 'Vector / Scalable'
          : Math.round(dpi) +
            ' DPI'
      );

      var locationSelect =
        query(
          card,
          '[data-ts-location-select]'
        );

      var selectedOption =
        locationSelect &&
        locationSelect.selectedIndex >= 0
          ? locationSelect.options[
              locationSelect.selectedIndex
            ]
          : null;

      var maxWidth =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-width'
              )
            )
          : 0;

      var maxHeight =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-height'
              )
            )
          : 0;

      var exceedsLocation =
        Boolean(
          (
            maxWidth &&
            width >
              maxWidth
          ) ||
          (
            maxHeight &&
            height >
              maxHeight
          )
        );

      if (
        exceedsLocation
      ) {
        updateQuality(
          state,
          'warning',
          'Exceeds location',
          'Selected print size is too large',
          'Reduce the width or height to fit the standard maximum for the selected imprint location.'
        );

        return;
      }

      if (
        state.isVector
      ) {
        updateQuality(
          state,
          'success',
          'Vector artwork',
          'Ready for print production',
          'Vector artwork can scale cleanly without losing edge sharpness.'
        );

        return;
      }

      if (
        dpi >= 250
      ) {
        updateQuality(
          state,
          'success',
          'Print-ready resolution',
          'Ready for print production',
          'This artwork has sufficient resolution for the entered print size.'
        );
      } else if (
        dpi >= 150
      ) {
        updateQuality(
          state,
          'info',
          'Usable resolution',
          'This artwork may be usable',
          'A larger or vector file may produce a sharper result, especially for fine details.'
        );
      } else if (
        dpi >= 100
      ) {
        updateQuality(
          state,
          'warning',
          'Low resolution',
          'Higher-resolution artwork recommended',
          'This artwork may lose sharpness at the entered print size.'
        );
      } else {
        updateQuality(
          state,
          'danger',
          'Very low resolution',
          'This artwork may print blurry',
          'This file may appear pixelated at the entered print size. Upload a larger image or create an enhanced vector.'
        );
      }
    }

    function setDefaultPrintSize(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      if (
        !state.naturalWidth ||
        !state.naturalHeight
      ) {
        return;
      }

      var maximumWidth =
        11;

      var maximumHeight =
        14;

      var scale =
        Math.min(
          maximumWidth /
            state.naturalWidth,
          maximumHeight /
            state.naturalHeight
        );

      var width =
        state.naturalWidth *
        scale;

      var height =
        state.naturalHeight *
        scale;

      setInputValue(
        widthInput,
        roundToTwo(width)
      );

      setInputValue(
        heightInput,
        roundToTwo(height)
      );

      setText(
        query(
          card,
          '[data-ts-maximum-size]'
        ),
        state.isVector
          ? 'Scalable'
          : roundToTwo(
              state.naturalWidth /
              150
            ) +
            ' × ' +
            roundToTwo(
              state.naturalHeight /
              150
            ) +
            ' in'
      );

      updateSizeAndQuality(
        state
      );
    }

    function analyzeImageData(
      imageData
    ) {
      var data =
        imageData.data;

      var pixels =
        0;

      var transparentPixels =
        0;

      var clusters =
        [];

      var step =
        Math.max(
          1,
          Math.floor(
            Math.sqrt(
              (
                imageData.width *
                imageData.height
              ) /
              18000
            )
          )
        );

      function addCluster(
        red,
        green,
        blue
      ) {
        var match =
          null;

        var smallestDistance =
          Infinity;

        clusters.forEach(
          function (cluster) {
            var distance =
              colorDistance(
                red,
                green,
                blue,
                cluster
              );

            if (
              distance <
              smallestDistance
            ) {
              smallestDistance =
                distance;

              match =
                cluster;
            }
          }
        );

        if (
          match &&
          smallestDistance <= 70
        ) {
          var newCount =
            match.count + 1;

          match.red =
            (
              match.red *
                match.count +
              red
            ) / newCount;

          match.green =
            (
              match.green *
                match.count +
              green
            ) / newCount;

          match.blue =
            (
              match.blue *
                match.count +
              blue
            ) / newCount;

          match.count =
            newCount;
        } else {
          clusters.push({
            red:
              red,

            green:
              green,

            blue:
              blue,

            count:
              1
          });
        }
      }

      for (
        var y = 0;
        y < imageData.height;
        y += step
      ) {
        for (
          var x = 0;
          x < imageData.width;
          x += step
        ) {
          var index =
            (
              y *
              imageData.width +
              x
            ) * 4;

          var alpha =
            data[index + 3];

          if (
            alpha < 245
          ) {
            transparentPixels +=
              1;
          }

          if (
            alpha < 70
          ) {
            continue;
          }

          pixels +=
            1;

          addCluster(
            data[index],
            data[index + 1],
            data[index + 2]
          );
        }
      }

      var minimumCluster =
        Math.max(
          4,
          Math.floor(
            pixels *
            0.018
          )
        );

      var dominant =
        clusters
          .filter(
            function (cluster) {
              return (
                cluster.count >=
                minimumCluster
              );
            }
          )
          .sort(
            function (
              first,
              second
            ) {
              return (
                second.count -
                first.count
              );
            }
          );

      return {
        dominantColors:
          Math.max(
            1,
            dominant.length
          ),

        printableColors:
          dominant.length > 8
            ? 9
            : Math.max(
                1,
                dominant.length
              ),

        hasTransparency:
          transparentPixels > 0
      };
    }

    async function analyzeFile(state) {
      var card =
        state.card;

      if (
        state.isVector
      ) {
        state.naturalWidth =
          1000;

        state.naturalHeight =
          1000;

        state.aspectRatio =
          1;

        state.analysis = {
          dominantColors:
            1,

          printableColors:
            1,

          hasTransparency:
            true
        };

        setText(
          query(
            card,
            '[data-ts-pixel-dimensions]'
          ),
          'Vector artwork'
        );

        setText(
          query(
            card,
            '[data-ts-transparency-status]'
          ),
          'Supported'
        );

        setText(
          query(
            card,
            '[data-ts-dominant-colors]'
          ),
          'Vector artwork'
        );

        setText(
          query(
            card,
            '[data-ts-printable-colors]'
          ),
          'Production review'
        );

        setDefaultPrintSize(
          state
        );

        return;
      }

      var image =
        await loadImageFromUrl(
          state.originalUrl
        );

      state.naturalWidth =
        image.naturalWidth;

      state.naturalHeight =
        image.naturalHeight;

      state.aspectRatio =
        state.naturalWidth /
        state.naturalHeight;

      var maximumSize =
        300;

      var scale =
        Math.min(
          1,
          maximumSize /
            Math.max(
              state.naturalWidth,
              state.naturalHeight
            )
        );

      var canvas =
        document.createElement(
          'canvas'
        );

      canvas.width =
        Math.max(
          1,
          Math.round(
            state.naturalWidth *
            scale
          )
        );

      canvas.height =
        Math.max(
          1,
          Math.round(
            state.naturalHeight *
            scale
          )
        );

      var context =
        canvas.getContext(
          '2d',
          {
            willReadFrequently:
              true
          }
        );

      context.drawImage(
        image,
        0,
        0,
        canvas.width,
        canvas.height
      );

      var imageData =
        context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );

      state.analysis =
        analyzeImageData(
          imageData
        );

      setText(
        query(
          card,
          '[data-ts-pixel-dimensions]'
        ),
        state.naturalWidth +
          ' × ' +
          state.naturalHeight +
          ' px'
      );

      setText(
        query(
          card,
          '[data-ts-transparency-status]'
        ),
        state.analysis.hasTransparency
          ? 'Detected'
          : 'Solid background'
      );

      setText(
        query(
          card,
          '[data-ts-dominant-colors]'
        ),
        state.analysis.dominantColors > 8
          ? 'Full color / blended'
          : state.analysis.dominantColors +
            (
              state.analysis.dominantColors === 1
                ? ' color'
                : ' colors'
            )
      );

      setText(
        query(
          card,
          '[data-ts-printable-colors]'
        ),
        state.analysis.printableColors > 8
          ? 'Full color'
          : state.analysis.printableColors +
            (
              state.analysis.printableColors === 1
                ? ' color'
                : ' colors'
            )
      );

      setDefaultPrintSize(
        state
      );
    }

    function syncBackgroundRefusal(state) {
      var refused = Boolean(state.sourceByteHash && backgroundRefusals.has(state.sourceByteHash));
      var actions = query(state.card, '.ts-background-actions');
      if (!actions) return;
      var panel = query(state.card, '[data-ts-background-refusal]');
      if (!panel) {
        panel = document.createElement('section');
        panel.setAttribute('data-ts-background-refusal', ''); panel.setAttribute('role', 'status');
        var heading = document.createElement('h5'); heading.textContent = 'Background too complex';
        var detail = document.createElement('p'); detail.textContent = BACKGROUND_REFUSAL;
        panel.appendChild(heading); panel.appendChild(detail);
        actions.parentNode.insertBefore(panel, actions);
      }
      panel.hidden = !refused;
      queryAll(state.card, '[data-ts-remove-white],[data-ts-apply-background-removal]').forEach(function (button) {
        button.disabled = refused;
      });
    }

    async function handleFileSelection(
      state,
      file
    ) {
      var card =
        state.card;

      if (!file) return;

      if (
        file.size >
        MAX_FILE_SIZE
      ) {
        window.alert(
          'This artwork file is larger than the 10 MB limit.'
        );

        return;
      }

      if (
        !isSvgFile(file) &&
        !isSupportedRaster(file)
      ) {
        window.alert(
          'Choose a PNG, JPG, JPEG, WebP, GIF, BMP, or SVG artwork file.'
        );

        return;
      }

      var selectionToken = (state.backgroundSelectionToken || 0) + 1;
      state.backgroundSelectionToken = selectionToken;
      var sourceByteHash;
      try { sourceByteHash = await pixelDigest(await file.arrayBuffer()); }
      catch (error) {
        serviceBoundary(state, 'background-removal', 'service-source', 'source_load_failed');
        return;
      }
      if (state.backgroundSelectionToken !== selectionToken) return;
      state.sourceByteHash = sourceByteHash;
      card.setAttribute('data-ts-background-source-sha256', sourceByteHash);
      syncBackgroundRefusal(state);

      resetStateUrls(
        state
      );

      state.file =
        file;

      state.isVector =
        isSvgFile(
          file
        );

      state.originalUrl =
        createObjectUrl(
          file
        );

      state.currentPreviewUrl =
        state.originalUrl;

      state.backgroundUrl =
        '';

      state.vectorUrl =
        '';

      state.originalStoredUrl =
        '';

      state.backgroundStoredUrl =
        '';

      state.backgroundBlob =
        null;

      state.selectedVersion =
        'original';

      card.classList.add(
        'has-file'
      );

      setHidden(
        query(
          card,
          '[data-ts-upload-area]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-artwork-workspace]'
        ),
        false
      );

      setText(
        query(
          card,
          '[data-ts-artwork-title]'
        ),
        file.name
      );

      setText(
        query(
          card,
          '[data-ts-file-status]'
        ),
        'File selected'
      );

      setText(
        query(
          card,
          '[data-ts-file-type]'
        ),
        state.isVector
          ? 'SVG'
          : (
              file.type ||
              getFileExtension(
                file.name
              ).toUpperCase()
            )
      );

      setText(
        query(
          card,
          '[data-ts-file-size]'
        ),
        formatFileSize(
          file.size
        )
      );

      [
        '[data-ts-artwork-preview-image]',
        '[data-ts-version-original-image]'
      ].forEach(
        function (selector) {
          var image =
            query(
              card,
              selector
            );

          if (image) {
            image.src =
              state.originalUrl;
          }
        }
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="background_removed"]'
        ),
        true
      );

      setHidden(
        query(
          card,
          '[data-ts-version-option="vector"]'
        ),
        true
      );

      var vectorButton =
        query(
          card,
          '[data-ts-create-vector]'
        );

      if (vectorButton) {
        vectorButton.disabled =
          state.isVector;

        setText(
          query(
            card,
            '[data-ts-create-vector-text]'
          ),
          state.isVector
            ? 'Artwork Is Already Vector'
            : 'Create Enhanced Vector'
        );
      }

      selectVersion(
        state,
        'original'
      );

      activateNextCard(
        state
      );

      updateSummary();

      try {
        await analyzeFile(
          state
        );
      } catch (error) {
        console.error(
          'Artwork analysis error:',
          error
        );
      }

      storeOriginalFile(
        state
      );
    }

    async function detectBackgroundColor(
      state
    ) {
      if (
        !state.file ||
        state.isVector
      ) {
        return;
      }

      try {
        var image =
          await loadImageFromUrl(
            state.originalUrl
          );

        var canvas =
          document.createElement(
            'canvas'
          );

        canvas.width =
          image.naturalWidth;

        canvas.height =
          image.naturalHeight;

        var context =
          canvas.getContext(
            '2d',
            {
              willReadFrequently:
                true
            }
          );

        context.drawImage(
          image,
          0,
          0
        );

        var imageData =
          context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

        var detection =
          buildEdgeConnectedBackgroundMask(
            imageData,
            canvas.width,
            canvas.height,
            {
              tolerance: 32,
              cleanup: 0
            }
          );

        var detected =
          detection.backgroundColor;

        setInputValue(
          query(
            state.card,
            '[data-ts-background-color]'
          ),
          detected
        );

        return detection;
      } catch (error) {
        console.error(
          'Background detection error:',
          error
        );

        return null;
      }
    }

    async function applyBackgroundRemoval(
      state
    ) {
      if (state.artworkServiceBusy) return;
      if (
        !state.file ||
        state.isVector
      ) {
        serviceBoundary(state, 'background-removal', 'service-source', 'service_input_unavailable');
        window.alert(
          'Background removal is available for PNG, JPG, JPEG, WebP, GIF, and BMP files.'
        );

        return;
      }

      var card =
        state.card;

      var automaticDetection =
        card.getAttribute(
          'data-ts-auto-detect-background-request'
        ) ===
        'true';

      if (
        automaticDetection
      ) {
        card.removeAttribute(
          'data-ts-auto-detect-background-request'
        );
      }

      var colorInput =
        query(
          card,
          '[data-ts-background-color]'
        );

      var toleranceInput =
        query(
          card,
          '[data-ts-background-tolerance]'
        );

      var cleanupInput =
        query(
          card,
          '[data-ts-edge-cleanup]'
        );

      var target =
        hexToRgb(
          colorInput
            ? colorInput.value
            : '#ffffff'
        );

      var tolerance =
        Number(
          toleranceInput
            ? toleranceInput.value
            : 32
        );

      var cleanup =
        Number(
          cleanupInput
            ? cleanupInput.value
            : 1
        );

      state.artworkServiceBusy = true;
      backgroundValidationRecords.delete(card);
      var boundary = 'service-source';
      serviceBoundary(state, 'background-removal', boundary);
      try {
        var sourceFile = state.file;
        var sourceByteHash = await pixelDigest(await sourceFile.arrayBuffer());
        if (state.file !== sourceFile || sourceByteHash !== state.sourceByteHash) throw new Error('The background-removal source changed. Nothing was processed.');
        if (backgroundRefusals.has(sourceByteHash)) throw backgroundUnsupportedError();
        var sourceUrl = state.originalUrl;
        function requireCurrentSource() {
          if (state.file !== sourceFile || state.sourceByteHash !== sourceByteHash || state.originalUrl !== sourceUrl) {
            var changed = new Error('The background-removal source changed. No stale result was applied.');
            changed.errorClass = 'source_load_failed'; throw changed;
          }
        }
        var image =
          await loadImageFromUrl(
            sourceUrl
          );
        requireCurrentSource();

        var canvas =
          document.createElement(
            'canvas'
          );

        canvas.width =
          image.naturalWidth;

        canvas.height =
          image.naturalHeight;

        var context =
          canvas.getContext(
            '2d',
            {
              willReadFrequently:
                true
            }
          );

        context.drawImage(
          image,
          0,
          0
        );

        var imageData =
          context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

        var sourcePixelHash = await pixelDigest(imageData.data);
        var capture = window.NovaUpscaleSafeDiagnostics;
        if (capture && capture.enabled) capture.capture('service-source', {
          tool:'background-removal',sourceHash:await pixelDigest(await state.file.arrayBuffer()),
          fileType:state.file.type,bytes:state.file.size,width:canvas.width,height:canvas.height
        });
        boundary = 'service-processing'; serviceBoundary(state, 'background-removal', boundary);
        // Desktop manual-color controls and mobile automatic removal share the
        // same source eligibility screen. A manual color choice is not allowed
        // to bypass the simple-background boundary.
        buildEdgeConnectedBackgroundMask(imageData, canvas.width, canvas.height, {
          assessmentOnly: true, tolerance: 32, cleanup: 1
        });
        var maskEvidence;
        try {
          maskEvidence = buildEdgeConnectedBackgroundMask(
            imageData,
            canvas.width,
            canvas.height,
            {
              preferredTarget:
                target,
              preferTarget:
                !automaticDetection,
              tolerance:
                tolerance,
              cleanup:
                cleanup
            }
          );
        } catch (maskError) {
          if (!automaticDetection && maskError.errorClass === 'background_unsupported_input') {
            maskError = new Error('The selected background color could not be separated safely. Choose the surrounding background color or continue with Original.');
            maskError.errorClass = 'result_mismatch';
          }
          throw maskError;
        }

        setInputValue(
          colorInput,
          maskEvidence.backgroundColor
        );

        if (
          window.NovaMobileReleaseGate &&
          typeof window.NovaMobileReleaseGate.record ===
            'function'
        ) {
          window.NovaMobileReleaseGate.record(
            'twisted-swag-background-mask-built',
            maskEvidence
          );
        }

        context.putImageData(
          imageData,
          0,
          0
        );

        var blob =
          await canvasToBlob(
            canvas,
            'image/png',
            1
          );
        requireCurrentSource();

        revokeObjectUrl(
          state.backgroundUrl
        );

        state.backgroundBlob =
          blob;

        state.backgroundUrl =
          createObjectUrl(
            blob
          );

        state.currentPreviewUrl =
          state.backgroundUrl;

        var mainPreview =
          query(
            card,
            '[data-ts-artwork-preview-image]'
          );

        var backgroundPreview =
          query(
            card,
            '[data-ts-version-background-image]'
          );

        if (mainPreview) {
          mainPreview.src =
            state.backgroundUrl;
        }

        if (backgroundPreview) {
          backgroundPreview.src =
            state.backgroundUrl;
        }

        // Bind the existing edge-connected mask's confidence to both exact decoded
        // pixel buffers. No semantic "most border pixels must be background"
        // assumption is made when foreground legitimately meets an image edge.
        backgroundValidationRecords.set(card, {
          version:'edge-connected-mask-v1', originalPreview:state.originalUrl, outputPreview:state.backgroundUrl,
          sourcePixelHash:sourcePixelHash,
          outputPixelHash:await pixelDigest(context.getImageData(0, 0, canvas.width, canvas.height).data),
          width:canvas.width, height:canvas.height, confidence:maskEvidence.confidence === true,
          removedPixelCount:maskEvidence.removedPixelCount,
          connectedRemovedPixelCount:maskEvidence.connectedRemovedPixelCount
        });

        setHidden(
          query(
            card,
            '[data-ts-version-option="background_removed"]'
          ),
          false
        );

        var restoreButton =
          query(
            card,
            '[data-ts-restore-original]'
          );

        if (restoreButton) {
          restoreButton.disabled =
            false;
        }

        setInputValue(
          query(
            card,
            '[data-ts-property-background-status]'
          ),
          'Yes'
        );

        boundary = 'service-storage'; serviceBoundary(state, 'background-removal', boundary);
        state.backgroundStoredUrl =
          await uploadStoredFile(
            state,
            blob,
            'background-removed',
            'artwork-' +
              state.number +
              '-background-removed.png'
          );

        selectVersion(
          state,
          'background_removed'
        );
        serviceBoundary(state, 'background-removal', 'service-result');
      } catch (error) {
        if (error.errorClass === 'background_unsupported_input' && sourceByteHash && state.file === sourceFile) {
          rememberBackgroundRefusal(sourceByteHash);
        }
        var failureClass = boundary === 'service-storage' && (error.status === 401 || error.status === 403)
          ? 'authorization_failed' : error.errorClass || (boundary === 'service-storage' ? 'storage_failed' : boundary === 'service-source' ? 'source_load_failed' : 'processing_failed');
        serviceBoundary(state, 'background-removal', boundary, failureClass, error.status, error.name, error.novaArtworkValidationRule);
        console.error(
          'Background removal failed at:',
          boundary
        );

        if (!automaticDetection && error.errorClass !== 'background_unsupported_input') window.alert(
          error &&
          error.message
            ? error.message
            : 'The background could not be removed. Your original artwork remains safe.'
        );
      } finally { state.artworkServiceBusy = false; }
    }

    function restoreOriginal(state) {
      if (!state.file) return;

      state.currentPreviewUrl =
        state.originalUrl;

      var preview =
        query(
          state.card,
          '[data-ts-artwork-preview-image]'
        );

      if (preview) {
        preview.src =
          state.originalUrl;
      }

      selectVersion(
        state,
        'original'
      );
    }

    function updateVectorProgress(
      state,
      percent,
      message
    ) {
      if (message) state.vectorProgressMessage = message;
      message = state.vectorProgressMessage || 'Preparing artwork…';
      var card =
        state.card;

      var progress =
        query(
          card,
          '[data-ts-vector-progress]'
        );

      setHidden(
        progress,
        false
      );

      if (progress) progress.setAttribute('aria-busy', percent === 100 ? 'false' : 'true');
      var messageNode = query(card, '[data-ts-vector-progress-message]');
      if (messageNode) {
        messageNode.setAttribute('role', 'status');
        if (messageNode.textContent !== message) setText(messageNode, message);
      }
      var elapsedNode = query(card, '[data-ts-vector-progress-percent]');
      if (elapsedNode) elapsedNode.setAttribute('aria-live', 'off');

      setText(
        query(
          card,
          '[data-ts-vector-progress-percent]'
        ),
        percent === 100 ? 'Result received' : 'Elapsed ' + Math.floor((Date.now() - state.vectorProgressStarted) / 1000) + 's'
      );

      var fill =
        query(
          card,
          '[data-ts-vector-progress-fill]'
        );

      if (fill) {
        fill.style.width = percent === 100 ? '100%' : '30%';
        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (state.vectorProgressAnimation && (reduced || percent === 100)) { state.vectorProgressAnimation.cancel(); state.vectorProgressAnimation = null; }
        if (percent !== 100 && !reduced && !state.vectorProgressAnimation && typeof fill.animate === 'function') {
          state.vectorProgressAnimation = fill.animate([{ transform:'translateX(-100%)' }, { transform:'translateX(330%)' }], { duration:1400, iterations:Infinity });
        }
      }
    }

    function startVectorProgress(state) {
      clearProgressTimer(
        state
      );

      state.vectorProgressStarted = Date.now();

      updateVectorProgress(
        state,
        null,
        'Submitting artwork and waiting for the vector service…'
      );

      state.progressTimer =
        window.setInterval(
          function () {
            updateVectorProgress(
              state,
              null,
              state.vectorProgressMessage
            );
          },
          1000
        );
    }

    async function createVector(state, explicitSource) {
      if (state.artworkServiceBusy) {
        if (explicitSource) throw new Error('An artwork operation is already running. No duplicate was submitted.');
        return;
      }
      if (
        !explicitSource && (!state.file || state.isVector)
      ) {
        serviceBoundary(state, 'vectorization', 'service-source', 'service_input_unavailable');
        return;
      }
      // The mobile adapter supplies the exact verified candidate bytes. A
      // version label alone must never silently select this card's newer File.
      if (explicitSource && (!(explicitSource.blob instanceof Blob) ||
          !explicitSource.blob.size || explicitSource.blob.size > MAX_FILE_SIZE ||
          SUPPORTED_RASTER_TYPES.indexOf(explicitSource.blob.type) < 0 ||
          ['original', 'background_removed'].indexOf(explicitSource.type) < 0 ||
          !/^[a-f0-9]{64}$/.test(explicitSource.hash))) {
        serviceBoundary(state, 'vectorization', 'service-source', 'service_input_unavailable');
        throw new Error('The selected vectorization source is unavailable. Nothing was submitted.');
      }
      state.artworkServiceBusy = true;
      if (explicitSource) {
        try {
          var digest = await crypto.subtle.digest('SHA-256', await explicitSource.blob.arrayBuffer());
          var actual = Array.from(new Uint8Array(digest), function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
          if (actual !== explicitSource.hash) throw new Error('The selected vectorization source checksum changed. Nothing was submitted.');
        } catch (error) {
          state.artworkServiceBusy = false;
          serviceBoundary(state, 'vectorization', 'service-source', 'result_mismatch');
          throw error;
        }
      }

      var card =
        state.card;

      var button =
        query(
          card,
          '[data-ts-create-vector]'
        );

      var buttonText =
        query(
          card,
          '[data-ts-create-vector-text]'
        );

      setHidden(
        query(
          card,
          '[data-ts-vector-error]'
        ),
        true
      );

      if (button) {
        button.disabled =
          true;
      }

      setText(
        buttonText,
        'Creating Enhanced Vector…'
      );

      startVectorProgress(
        state
      );

      var requestedVersion = card.getAttribute('data-ts-vector-source-version');
      card.removeAttribute('data-ts-vector-source-version');
      var useBackground = (requestedVersion || state.selectedVersion) === 'background_removed' && state.backgroundBlob;
      var sourceFile = explicitSource ? explicitSource.blob : (useBackground ? state.backgroundBlob : state.file);

      state.artworkServiceBusy = true;
      var boundary = 'service-processing', responseStatus;
      serviceBoundary(state, 'vectorization', boundary);
      try {
        var privateApi = window.NovaAiGenerationAPI;
        if (!privateApi || typeof privateApi.projectArtworkService !== 'function') throw new Error('Reopen this project in the updated Design Studio to vectorize securely.');
        var data = await privateApi.projectArtworkService(sourceFile, true, explicitSource && explicitSource.hash, undefined, function (message) { updateVectorProgress(state, null, message); });
        card.setAttribute('data-ts-private-vector-artifact', JSON.stringify(data.privateArtifact));
        responseStatus = 200;
        boundary = 'service-response'; serviceBoundary(state, 'vectorization', boundary, null, responseStatus);

        if (
          !data ||
          data.success !==
            true ||
          !data.vectorUrl
        ) {
          throw new Error(
            data &&
            data.error
              ? data.error
              : 'The enhanced vector could not be created.'
          );
        }

        clearProgressTimer(
          state
        );

        updateVectorProgress(
          state,
          100,
          'Vector service response received. Review the resulting artwork.'
        );

        state.vectorUrl =
          data.vectorUrl;

        var vectorPreview =
          query(
            card,
            '[data-ts-version-vector-image]'
          );

        if (vectorPreview) {
          vectorPreview.src =
            data.previewUrl;
        }

        setHidden(
          query(
            card,
            '[data-ts-version-option="vector"]'
          ),
          false
        );

        setInputValue(
          query(
            card,
            '[data-ts-property-vector-url]'
          ),
          state.vectorUrl
        );

        setText(
          buttonText,
          'Enhanced Vector Created'
        );

        selectVersion(
          state,
          'vector'
        );
        serviceBoundary(state, 'vectorization', 'service-result', null, responseStatus);

        window.setTimeout(
          function () {
            setHidden(
              query(
                card,
                '[data-ts-vector-progress]'
              ),
              true
            );
          },
          600
        );
      } catch (error) {
        serviceBoundary(state, 'vectorization', boundary, boundary === 'service-response' ? (responseStatus >= 400 ? 'http_error' : 'invalid_response') : 'request_failed', responseStatus, error.name);
        clearProgressTimer(
          state
        );

        setHidden(
          query(
            card,
            '[data-ts-vector-progress]'
          ),
          true
        );

        var errorBox =
          query(
            card,
            '[data-ts-vector-error]'
          );

        setHidden(
          errorBox,
          false
        );

        setText(
          query(
            card,
            '[data-ts-vector-error-message]'
          ),
          error &&
          error.message
            ? error.message
            : 'Your original artwork remains available.'
        );

        if (button) {
          button.disabled =
            false;
        }

        setText(
          buttonText,
          'Check Existing Vector Result'
        );
      } finally { state.artworkServiceBusy = false; }
    }

    function handleLocationChange(state) {
      var card =
        state.card;

      var select =
        query(
          card,
          '[data-ts-location-select]'
        );

      var selectedOption =
        select &&
        select.selectedIndex >= 0
          ? select.options[
              select.selectedIndex
            ]
          : null;

      var maxWidth =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-width'
              )
            )
          : 0;

      var maxHeight =
        selectedOption
          ? parseFloat(
              selectedOption.getAttribute(
                'data-max-height'
              )
            )
          : 0;

      if (
        maxWidth &&
        maxHeight &&
        state.aspectRatio
      ) {
        var width =
          maxWidth;

        var height =
          width /
          state.aspectRatio;

        if (
          height >
          maxHeight
        ) {
          height =
            maxHeight;

          width =
            height *
            state.aspectRatio;
        }

        setInputValue(
          query(
            card,
            '[data-ts-print-width]'
          ),
          roundToTwo(width)
        );

        setInputValue(
          query(
            card,
            '[data-ts-print-height]'
          ),
          roundToTwo(height)
        );
      }

      setInputValue(
        query(
          card,
          '[data-ts-property-location]'
        ),
        select
          ? select.value
          : ''
      );

      updateSizeAndQuality(
        state
      );
    }

    function handleWidthChange(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var width =
        parseFloat(
          widthInput
            ? widthInput.value
            : ''
        );

      if (
        state.isLocked &&
        width > 0 &&
        state.aspectRatio
      ) {
        setInputValue(
          heightInput,
          roundToTwo(
            width /
            state.aspectRatio
          )
        );
      }

      updateSizeAndQuality(
        state
      );
    }

    function handleHeightChange(state) {
      var card =
        state.card;

      var widthInput =
        query(
          card,
          '[data-ts-print-width]'
        );

      var heightInput =
        query(
          card,
          '[data-ts-print-height]'
        );

      var height =
        parseFloat(
          heightInput
            ? heightInput.value
            : ''
        );

      if (
        state.isLocked &&
        height > 0 &&
        state.aspectRatio
      ) {
        setInputValue(
          widthInput,
          roundToTwo(
            height *
            state.aspectRatio
          )
        );
      }

      updateSizeAndQuality(
        state
      );
    }

    function toggleAspectLock(state) {
      state.isLocked =
        !state.isLocked;

      var button =
        query(
          state.card,
          '[data-ts-aspect-lock]'
        );

      if (button) {
        button.classList.toggle(
          'is-locked',
          state.isLocked
        );

        button.setAttribute(
          'aria-pressed',
          state.isLocked
            ? 'true'
            : 'false'
        );

        button.setAttribute(
          'aria-label',
          state.isLocked
            ? 'Unlock artwork proportions'
            : 'Lock artwork proportions'
        );
      }

      setText(
        query(
          state.card,
          '[data-ts-aspect-lock-text]'
        ),
        state.isLocked
          ? 'Locked'
          : 'Unlocked'
      );
    }

    studio.addEventListener(
      'click',
      function (event) {
        var tooltipButton =
          event.target.closest(
            '[data-ts-tooltip-button]'
          );

        if (tooltipButton) {
          event.preventDefault();
          event.stopPropagation();

          toggleTooltip(
            tooltipButton
          );

          return;
        }

        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        var replaceButton =
          event.target.closest(
            '[data-ts-replace-artwork]'
          );

        if (replaceButton) {
          event.preventDefault();

          var fileInput =
            query(
              state.card,
              '[data-ts-artwork-input]'
            );

          if (fileInput) {
            fileInput.value =
              '';

            fileInput.click();
          }

          return;
        }

        var removeButton =
          event.target.closest(
            '[data-ts-remove-artwork]'
          );

        if (removeButton) {
          event.preventDefault();

          clearCardUi(
            state
          );

          return;
        }

        var previewButton =
          event.target.closest(
            '[data-ts-open-preview]'
          );

        if (previewButton) {
          event.preventDefault();

          openModal(
            state.currentPreviewUrl ||
            state.originalUrl,
            state.file
              ? state.file.name
              : 'Artwork Preview'
          );

          return;
        }

        var whiteButton =
          event.target.closest(
            '[data-ts-remove-white]'
          );

        if (whiteButton) {
          event.preventDefault();

          setInputValue(
            query(
              state.card,
              '[data-ts-background-color]'
            ),
            '#ffffff'
          );

          applyBackgroundRemoval(
            state
          );

          return;
        }

        var detectButton =
          event.target.closest(
            '[data-ts-detect-background]'
          );

        if (detectButton) {
          event.preventDefault();

          detectBackgroundColor(
            state
          );

          return;
        }

        var applyButton =
          event.target.closest(
            '[data-ts-apply-background-removal]'
          );

        if (applyButton) {
          event.preventDefault();

          applyBackgroundRemoval(
            state
          );

          return;
        }

        var restoreButton =
          event.target.closest(
            '[data-ts-restore-original]'
          );

        if (restoreButton) {
          event.preventDefault();

          restoreOriginal(
            state
          );

          return;
        }

        var vectorButton =
          event.target.closest(
            '[data-ts-create-vector]'
          );

        if (vectorButton) {
          event.preventDefault();

          createVector(
            state
          );

          return;
        }

        var lockButton =
          event.target.closest(
            '[data-ts-aspect-lock]'
          );

        if (lockButton) {
          event.preventDefault();

          toggleAspectLock(
            state
          );
        }
      }
    );

    studio.addEventListener(
      'change',
      function (event) {
        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        if (
          event.target.matches(
            '[data-ts-artwork-input]'
          )
        ) {
          if (
            event.target.files &&
            event.target.files.length
          ) {
            handleFileSelection(
              state,
              event.target.files[0]
            );
          }

          return;
        }

        if (
          event.target.matches(
            '[data-ts-location-select]'
          )
        ) {
          handleLocationChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-version-choice]'
          )
        ) {
          selectVersion(
            state,
            event.target.value
          );
        }
      }
    );

    studio.addEventListener(
      'input',
      function (event) {
        var state =
          getStateFromElement(
            event.target
          );

        if (!state) return;

        if (
          event.target.matches(
            '[data-ts-print-width]'
          )
        ) {
          handleWidthChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-print-height]'
          )
        ) {
          handleHeightChange(
            state
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-background-tolerance]'
          )
        ) {
          setText(
            query(
              state.card,
              '[data-ts-background-tolerance-value]'
            ),
            event.target.value
          );

          return;
        }

        if (
          event.target.matches(
            '[data-ts-edge-cleanup]'
          )
        ) {
          setText(
            query(
              state.card,
              '[data-ts-edge-cleanup-value]'
            ),
            event.target.value
          );
        }
      }
    );

    document.addEventListener(
      'click',
      function (event) {
        if (
          !event.target.closest(
            '[data-ts-tooltip-button]'
          ) &&
          !event.target.closest(
            '[data-ts-tooltip]'
          )
        ) {
          closeAllTooltips();
        }
      }
    );

    if (modalClose) {
      modalClose.addEventListener(
        'click',
        closeModal
      );
    }

    if (modal) {
      modal.addEventListener(
        'click',
        function (event) {
          if (
            event.target ===
            modal
          ) {
            closeModal();
          }
        }
      );
    }

    document.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key ===
          'Escape'
        ) {
          closeAllTooltips();

          if (
            modal &&
            modal.classList.contains(
              'is-open'
            )
          ) {
            closeModal();
          }
        }
      }
    );

    window.addEventListener(
      'beforeunload',
      function () {
        states.forEach(
          function (state) {
            clearProgressTimer(
              state
            );

            resetStateUrls(
              state
            );
          }
        );
      }
    );

    movePropertiesIntoProductForm(
      studio
    );

    states.forEach(
      function (state) {
        vectorDispatchers.set(state.card, function (source) { return createVector(state, source); });
        backgroundRefusalSyncers.set(state.card, function () { syncBackgroundRefusal(state); });
        clearProperties(
          state
        );

        selectVersion(
          state,
          'original'
        );
      }
    );

    updateSummary();
  }

  function initializeAllStudios() {
    document
      .querySelectorAll(
        '[data-ts-artwork-studio]'
      )
      .forEach(
        initializeStudio
      );
  }

  window.TwistedSwagArtworkStudioAPI = {
    controllerVersion: 'source-bound-v1',
    initializeAllStudios: initializeAllStudios,
    getBackgroundAssessment: function (card) {
      var hash = card && card.getAttribute('data-ts-background-source-sha256');
      return /^[a-f0-9]{64}$/.test(hash || '') ? { sourceHash: hash, unsupported: backgroundRefusals.has(hash) } : null;
    },
    assessBackgroundPixels: async function (bytes, imageData, width, height) {
      var hash = await pixelDigest(bytes);
      try {
        if (backgroundRefusals.has(hash)) throw backgroundUnsupportedError();
        buildEdgeConnectedBackgroundMask(imageData, width, height, { assessmentOnly: true, tolerance: 32, cleanup: 1 });
        return { sourceHash: hash, unsupported: false };
      } catch (error) {
        if (error.errorClass === 'background_unsupported_input') rememberBackgroundRefusal(hash);
        error.sourceHash = hash;
        throw error;
      }
    },
    getBackgroundValidationEvidence: function (card, originalPreview, outputPreview) {
      var record = backgroundValidationRecords.get(card);
      return record && record.originalPreview === originalPreview && record.outputPreview === outputPreview
        ? Object.assign({}, record) : null;
    },
    vectorizeCandidate: function (card, source) {
      var dispatch = vectorDispatchers.get(card);
      if (!dispatch && card && typeof card.closest === 'function') {
        initializeStudio(card.closest('[data-ts-artwork-studio]'));
        dispatch = vectorDispatchers.get(card);
      }
      if (!dispatch) return Promise.reject(new Error('The existing artwork controller is unavailable.'));
      return dispatch(source);
    }
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllStudios
    );
  } else {
    initializeAllStudios();
  }

  window.addEventListener(
    'load',
    initializeAllStudios
  );

  document.addEventListener(
    'shopify:section:load',
    initializeAllStudios
  );
})();
